import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  Button,
  Form,
  Input,
  AutoComplete,
  Typography,
  Divider,
  Card,
  Skeleton,
  Avatar,
  Empty,
} from 'antd';
import { Axios } from 'src/lib';
import { FronteggContext } from '@frontegg/rest-api';
import { useDispatch } from 'react-redux';
import { updateEncounter } from '../../store/actions/encounter';
import { useMask } from '@react-input/mask';
import { format } from 'date-fns';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { generateColorFromName } from 'src/utils/misc';
import { showToastError } from 'src/utilities/errortoast';
import { showToastSuccess } from 'src/utilities/toast';
import { generateEncounterDescription } from 'src/utils/encounter';
import {
  usePatientListClient,
  useSearchPatientsInfinite,
} from 'src/hooks/patient';
import { useDebouncedValue } from 'src/hooks/misc';

const NEW_PATIENT_OPTION_VALUE = 'create-new-patient';

/**
 * Date can be 2015-05-15 or Mon, 15 Jun 2015 00:00:00 GMT
 */
const formatStaticDate = (date) => {
  if (!date) {
    return '';
  }

  const dateObj = new Date(date);
  if (isNaN(dateObj)) {
    return '';
  }

  const [day, month, year] = [
    String(dateObj.getUTCDate()).padStart(2, '0'),
    String(dateObj.getUTCMonth() + 1).padStart(2, '0'),
    dateObj.getUTCFullYear(),
  ];

  return `${month}/${day}/${year}`;
};

const fetchPatientDetails = async (patientId) => {
  const token = FronteggContext.getAccessToken();

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const response = await Axios.get(`/v2/patients/${patientId}`, {
    headers,
  });
  return response.data;
};

const createNewPatient = async (values) => {
  const token = FronteggContext.getAccessToken();

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const response = await Axios.post(`/v2/patients`, values, {
    headers,
  });
  return response.data;
};

const updatePatientDetails = async (patientId, values) => {
  const token = FronteggContext.getAccessToken();

  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const response = await Axios.put(`/v2/patients/${patientId}`, values, {
    headers,
  });
  return response.data;
};

const PatientDetails = ({ patientId, onDelete, onEdit }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    fetchPatientDetails(patientId)
      .then((data) => {
        setDetails(data);
      })
      .catch(() => {
        showToastError('Failed to fetch patient details');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [patientId]);

  if (isLoading) {
    return <Skeleton avatar paragraph={{ rows: 4 }} />;
  }

  if (!details) {
    return null;
  }

  const { first_name, last_name, patient_id, birth_date, ehr_id } = details;

  const nameParts = [first_name, last_name];
  const name = nameParts.filter(Boolean).join(' ');
  const initials = nameParts
    .map((part) => part?.[0])
    .join('')
    .toUpperCase();

  return (
    <div className="flex flex-col gap-8">
      <div className="flex gap-2">
        <Avatar
          style={{
            color: 'white',
            backgroundColor: generateColorFromName(name),
          }}
        >
          {initials}
        </Avatar>
        <div className="flex flex-col">
          <div className="font-medium">{name}</div>
          {patient_id && (
            <div className="text-sm text-gray-500">ID: {patient_id}</div>
          )}
          <div className="flex flex-row gap-2">
            {birth_date && (
              <div className="text-sm text-gray-500">
                DOB: {formatStaticDate(birth_date)}
              </div>
            )}
            {ehr_id && (
              <div className="text-sm text-gray-500">EHR/MRN ID: {ehr_id}</div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <span
          className="flex items-center gap-1 text-blue-500 cursor-pointer hover:text-blue-600 hover:underline"
          onClick={() => {
            onEdit({
              firstName: first_name,
              lastName: last_name,
              birthDate: birth_date,
            });
          }}
        >
          <EditOutlined />
          Edit patient
        </span>
        <span
          className="flex items-center gap-1 text-red-500 cursor-pointer hover:text-red-600 hover:underline"
          onClick={onDelete}
        >
          <DeleteOutlined />
          Remove from encounter
        </span>
      </div>
    </div>
  );
};

export const AddPatientModal = ({ isOpen, onClose, encounter, onUpdate }) => {
  const { patient, description } = encounter;
  const searchInputRef = useRef(null);
  const searchInputResetKeyRef = useRef(0);

  const [selectedPatient, setSelectedPatient] = useState(patient);
  const [patientForm] = Form.useForm();
  const values = Form.useWatch([], patientForm);
  const [editPatientFormVisible, setEditPatientFormVisible] = useState(false);
  const [newPatientFormVisible, setNewPatientFormVisible] = useState(false);
  const [patientFormValid, setPatientFormValid] = useState(false);
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { invalidate: invalidatePatientList } = usePatientListClient();

  const debouncedSearch = useDebouncedValue(search, 500);

  const {
    data: searchData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching: isSearching,
  } = useSearchPatientsInfinite({
    searchTerm: debouncedSearch,
    perPage: 20,
  });

  const searchResults = useMemo(() => {
    if (!debouncedSearch || !searchData?.pages) return [];

    const allPatients = searchData.pages.flatMap((page) => page.patients);

    const options = allPatients.map((patient) => ({
      ...patient,
      label: patient.patient_Name,
      value: patient.patient_id,
    }));

    if (options.length > 0) {
      options.push({
        label: NEW_PATIENT_OPTION_VALUE,
        value: NEW_PATIENT_OPTION_VALUE,
      });
    }

    return options;
  }, [searchData, debouncedSearch]);

  const inputRef = useMask({
    mask: '__/__/____',
    replacement: { _: /\d/ },
    showMask: true,
  });

  const updatePatientInEncounter = (data) => {
    const keyMap = {
      patientId: 'patient_id',
      description: 'description',
    };

    const payload = Object.entries(data).reduce((acc, [key, value]) => {
      acc[keyMap[key]] = value;
      return acc;
    }, {});

    return dispatch(
      updateEncounter(encounter.encounter_id, 'in-visit', payload, false)
    )
      .then((record) => {
        onUpdate?.(record);
      })
      .catch(() => {
        showToastError('Failed to add patient to encounter!');
      });
  };

  const updatePatient = async () => {
    const { firstName, lastName, birthDate, ...values } =
      await patientForm.validateFields();
    setIsLoading(true);
    await updatePatientDetails(selectedPatient.patient_id, {
      ...values,
      first_name: firstName,
      last_name: lastName,
      birth_date: format(new Date(birthDate), 'yyyy-MM-dd'),
    })
      .then(({ patient: updatedPatient }) => {
        const newName = [updatedPatient.first_name, updatedPatient.last_name]
          .filter(Boolean)
          .join(' ');

        setSelectedPatient({
          ...updatedPatient,
          patient_Name: newName,
        });
        const newEncounterDescription = generateEncounterDescription({
          prefix: newName,
        });

        setEditPatientFormVisible(false);

        if (newEncounterDescription !== description && patient) {
          return updatePatientInEncounter({
            patientId: updatedPatient.patient_id,
            description: newEncounterDescription,
          });
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const attachPatient = async () => {
    if (selectedPatient) {
      setIsLoading(true);
      await updatePatientInEncounter({
        patientId: selectedPatient?.patient_id,
        description: generateEncounterDescription({
          prefix: selectedPatient?.patient_Name,
        }),
      }).finally(() => {
        setIsLoading(false);
      });
      onClose();
      return;
    }

    setIsLoading(true);
    const { firstName, lastName, birthDate, ...values } =
      await patientForm.validateFields();
    await createNewPatient({
      ...values,
      first_name: firstName,
      last_name: lastName,
      birth_date: format(new Date(birthDate), 'yyyy-MM-dd'),
    })
      .then(({ patient: newPatient, message }) => {
        setSelectedPatient({
          ...newPatient,
          patient_Name: [newPatient.first_name, newPatient.last_name]
            .filter(Boolean)
            .join(' '),
        });
        showToastSuccess(message);
        invalidatePatientList();
      })
      .catch((e) => {
        showToastError('Failed to create patient');
        throw e;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const searchPatients = (val) => {
    setSearch(val);
  };

  const removePatient = () => {
    if (
      selectedPatient?.patient_id &&
      selectedPatient.patient_id === patient?.patient_id
    ) {
      const { destroy } = Modal.warn({
        footer: (
          <div className="flex justify-end gap-2 mt-4">
            <Button
              className="ih-button-secondary"
              type="button"
              onClick={() => destroy()}
            >
              Cancel
            </Button>
            <Button
              className="ih-button-negative"
              type="button"
              onClick={async () => {
                await updatePatientInEncounter({
                  patientId: null,
                  description: generateEncounterDescription(),
                });
                destroy();
                setSelectedPatient(null);
              }}
            >
              Proceed
            </Button>
          </div>
        ),
        title: 'Remove patient from encounter',
        content:
          'This will remove the patient from the encounter. You can reassign a patient later.',
      });
    } else {
      setSelectedPatient(null);
    }
  };

  const handleCreateNewPatient = ({ firstName, lastName, birthDate }) => {
    searchInputRef.current?.blur();
    searchInputResetKeyRef.current++;
    setSearch('');

    setNewPatientFormVisible(true);
    patientForm.setFieldsValue({
      firstName: firstName || '',
      lastName: lastName || '',
      birthDate: birthDate || '',
    });
  };

  const editSelectedPatient = ({ firstName, lastName, birthDate }) => {
    patientForm.setFieldsValue({
      firstName: firstName || '',
      lastName: lastName || '',
      birthDate: formatStaticDate(birthDate) || '',
    });
    setEditPatientFormVisible(true);
  };

  useEffect(() => {
    if (newPatientFormVisible || editPatientFormVisible) {
      patientForm
        .validateFields({ validateOnly: true })
        .then(() => setPatientFormValid(true))
        .catch(() => setPatientFormValid(false));
    } else {
      setPatientFormValid(false);
    }
  }, [values, patientForm, newPatientFormVisible, editPatientFormVisible]);

  useEffect(() => {
    setEditPatientFormVisible(false);
    setNewPatientFormVisible(false);
    setSearch('');
    // searchResults logic is now derived from debouncedSearch
  }, [!!selectedPatient]);

  const patientFormJSX = (
    <Form
      className="mt-4"
      form={patientForm}
      layout="vertical"
      size="large"
      autoComplete="off"
    >
      <Form.Item
        className="field-wrapper"
        name="firstName"
        label="First Name"
        rules={[{ required: true, message: 'First name is required' }]}
      >
        <Input className="field" placeholder="First Name" />
      </Form.Item>
      <Form.Item
        className="field-wrapper"
        name="lastName"
        label="Last Name"
        rules={[{ required: true, message: 'Last name is required' }]}
      >
        <Input className="field" placeholder="Last Name" />
      </Form.Item>
      <Form.Item
        className="field-wrapper"
        name="birthDate"
        label="Date of Birth"
        rules={[
          { required: true, message: 'Date of birth is required' },
          {
            pattern: /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/,
            message: 'Date of birth must be in the format MM/DD/YYYY',
          },
        ]}
      >
        <Input
          className="field"
          placeholder="MM/DD/YYYY"
          ref={(el) => {
            if (el) {
              inputRef.current = el?.nativeElement;
            }
          }}
        />
      </Form.Item>
    </Form>
  );

  const getModalBody = () => {
    if (selectedPatient) {
      if (editPatientFormVisible) {
        return (
          <>
            <div className="mt-4 font-medium">Edit patient</div>
            {patientFormJSX}
          </>
        );
      }

      return (
        <Card styles={{ body: { padding: `.75rem` } }}>
          <PatientDetails
            patientId={selectedPatient.patient_id}
            onDelete={removePatient}
            onEdit={editSelectedPatient}
          />
        </Card>
      );
    }

    return (
      <>
        <AutoComplete
          key={searchInputResetKeyRef.current}
          defaultValue={search}
          ref={searchInputRef}
          className="w-full"
          options={searchResults}
          open={dropdownOpen && search.length > 0}
          onDropdownVisibleChange={(visible) => setDropdownOpen(visible)}
          onPopupScroll={(e) => {
            const { target } = e;
            if (
              Math.abs(
                target.scrollHeight - target.scrollTop - target.offsetHeight
              ) < 1 &&
              hasNextPage &&
              !isFetchingNextPage
            ) {
              fetchNextPage();
            }
          }}
          dropdownRender={(menu) => (
            <>
              {menu}
              {isFetchingNextPage && (
                <div className="p-2 text-center text-gray-500">
                  Loading more...
                </div>
              )}
            </>
          )}
          optionRender={(option) => {
            const { data, value } = option;
            if (value === NEW_PATIENT_OPTION_VALUE) {
              return (
                <div>
                  <div className="flex items-center gap-1">
                    <PlusOutlined />
                    Create new patient
                  </div>
                  <div className="text-sm text-gray-500 flex gap-2">
                    {search}
                  </div>
                </div>
              );
            }
            const { patient_Name, appointment_date, patient_birth_date } = data;
            return (
              <div>
                {patient_Name}
                {patient_birth_date && (
                  <div className="text-sm text-gray-500 flex gap-2">
                    DOB:&nbsp;
                    {formatStaticDate(patient_birth_date)}
                  </div>
                )}
                {appointment_date && (
                  <div className="text-sm text-gray-500 flex gap-2">
                    Appointment Date:&nbsp;
                    {formatStaticDate(appointment_date)}
                  </div>
                )}
              </div>
            );
          }}
          onSelect={(value, option) => {
            setDropdownOpen(false);
            if (option.value === NEW_PATIENT_OPTION_VALUE) {
              const [firstName, ...rest] = search.split(' ');
              const lastName = rest.join(' ');
              handleCreateNewPatient({
                firstName,
                lastName,
                birthDate: '',
              });
            } else {
              setSelectedPatient(option);
            }
          }}
          notFoundContent={(() => {
            if (!search) {
              return <div>Try searching for a patient name</div>;
            }

            if (search.length < 3) {
              return <div>Please enter at least 3 characters</div>;
            }

            if (isSearching) {
              return <div>loading...</div>;
            }

            return (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div className="flex flex-col gap-1 text-center">
                    Couldn&apos;t find the patient with the term &quot;
                    {search}&quot;.
                    {!newPatientFormVisible && (
                      <div
                        className="cursor-pointer text-blue-500 hover:text-blue-600 hover:underline"
                        onClick={() => {
                          const [firstName, ...rest] = search.split(' ');
                          const lastName = rest.join(' ');
                          handleCreateNewPatient({
                            firstName,
                            lastName,
                            birthDate: '',
                          });
                        }}
                      >
                        Create a new patient.
                      </div>
                    )}
                  </div>
                }
              />
            );
          })()}
        >
          <Input.Search
            size="large"
            loading={isSearching}
            allowClear
            onFocus={() => setDropdownOpen(true)}
            onBlur={() => {
              setTimeout(() => setDropdownOpen(false), 200);
            }}
            onChange={(e) => {
              searchPatients(e.target.value);
            }}
            placeholder="Search patient"
          />
        </AutoComplete>

        {newPatientFormVisible && (
          <>
            <Divider style={{ marginTop: 24 }}>
              <p className="text-sm text-gray-500">OR</p>
            </Divider>

            <div className="mt-4 font-medium">Create new patient</div>
            {patientFormJSX}
          </>
        )}
      </>
    );
  };

  const getModalFooter = () => {
    if (selectedPatient && editPatientFormVisible) {
      return (
        <div className="flex justify-end gap-2 mt-8">
          <Button
            className="ih-button-secondary"
            onClick={() => setEditPatientFormVisible(false)}
          >
            Discard edit
          </Button>
          <Button
            disabled={isLoading || !patientFormValid}
            loading={isLoading}
            type="submit"
            className="ih-button-primary"
            onClick={updatePatient}
          >
            Update patient
          </Button>
        </div>
      );
    }

    if (!patient || !selectedPatient) {
      return (
        <div className="flex justify-end gap-2 mt-8">
          <Button className="ih-button-secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={isLoading || (!selectedPatient && !patientFormValid)}
            loading={isLoading}
            type="submit"
            className="ih-button-primary"
            onClick={attachPatient}
          >
            Update
          </Button>
        </div>
      );
    }

    return (
      <div className="flex justify-end gap-2 mt-8">
        <Button
          disabled={isLoading}
          className="ih-button-secondary"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    );
  };

  return (
    <Modal
      maskClosable={true}
      closable={true}
      open={isOpen}
      onCancel={onClose}
      title={
        <div className="flex flex-col mb-4">
          <Typography.Text>Assign patient to encounter</Typography.Text>
          <Typography.Text
            style={{ color: '#808080', fontWeight: '400' }}
            className="text-sm"
          >
            {description}
          </Typography.Text>
        </div>
      }
      footer={getModalFooter()}
    >
      {getModalBody()}
    </Modal>
  );
};

export const EncounterPatient = ({ encounter, onUpdate, size = 'sm' }) => {
  const { patient } = encounter;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const classes = {
    sm: 'text-[12px] gap-1',
    md: 'gap-2',
  };

  let trigger = null;

  if (!patient) {
    trigger = (
      <div
        onClick={() => {
          setIsModalOpen(true);
        }}
        className={`flex cursor-pointer items-center ${classes[size]}`}
      >
        <PlusOutlined /> <span>Add Patient</span>
      </div>
    );
  } else {
    const nameParts = [patient.first_name, patient.last_name];

    const name = nameParts.filter(Boolean).join(' ');
    trigger = (
      <div
        className={`flex cursor-pointer items-center ${classes[size]}`}
        onClick={() => {
          setIsModalOpen(true);
        }}
      >
        <UserOutlined />
        {name}
        <EditOutlined />
      </div>
    );
  }

  const stopPropagation = (e) => {
    e.stopPropagation();
    e.stopImmediatePropagation?.();
  };

  return (
    <div
      className="flex items-center gap-1"
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
    >
      {trigger}
      {isModalOpen && (
        <AddPatientModal
          encounter={encounter}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onUpdate={onUpdate}
        />
      )}
    </div>
  );
};
